import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Tabs, Input, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { FJ190, FJ191, FJ192, j208Api } from '../../services/j208Api';
import { BatchAcceptanceBridge, pwj208f1AcceptanceManifest } from '../../features/acceptance';
import { useJ208Mode } from './hooks/useJ208Mode';
import { useJ208Record } from './hooks/useJ208Record';
import { useJ208Navigation } from './hooks/useJ208Navigation';
import { SharedActionBar } from '../../components/SharedActionBar';
import { useModulePermission } from '../../hooks/usePermission';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Details from './components/Tab2Details';
import './J208Form.css';

const J208Form: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('1');
  const [selectedDetailIdx, setSelectedDetailIdx] = useState(0);
  const [searchText, setSearchText] = useState('');

  const { mode, setMode, isEditing } = useJ208Mode();
  const {
    record, formData, details, loading,
    setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    addDetailRow, deleteDetailRow, updateBoardNumbers,
  } = useJ208Record();

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useJ208Navigation(
    useCallback(async (id: string) => { await loadRecord(id); }, [loadRecord]),
  );

  const { canCreate, canEdit, canDelete } = useModulePermission('pwj208f1');

  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecord(ids[0]);
        }
      } catch {
        console.error('Failed to load J208 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- QuickSearch (client-side from loaded IDs) ---
  const handleSearch = useCallback(() => {
    const keyword = searchText.trim();
    if (!keyword) return;
    const idx = allIds.findIndex(id => id.includes(keyword));
    if (idx >= 0) {
      setCurrentIdx(idx);
      loadRecord(allIds[idx]);
    }
  }, [searchText, allIds, setCurrentIdx, loadRecord]);

  // --- CRUD handlers (split for SharedActionBar) ---
  const handleAdd = useCallback(() => {
    setMode('add');
    resetForAdd();
    setActiveTab('1');
  }, [setMode, resetForAdd]);

  const handleEdit = useCallback(() => {
    setMode('edit');
  }, [setMode]);

  const handleSaveAction = useCallback(async () => {
    await handleSave(mode, async (result: FJ190) => {
      if (mode === 'add') {
        const ids = await loadList();
        const newIdx = ids.indexOf(result.fj190_01);
        setCurrentIdx(newIdx >= 0 ? newIdx : 0);
      }
      applyResult(result);
      setMode('browse');
    });
  }, [mode, handleSave, loadList, setCurrentIdx, applyResult, setMode]);

  const handleCancel = useCallback(() => {
    revertToRecord();
    setMode('browse');
  }, [revertToRecord, setMode]);

  const handleDeleteAction = useCallback(() => {
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除繳庫單 ${record?.fj190_01} 嗎？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        await handleDelete(async () => {
          const ids = await loadList();
          if (ids.length > 0) {
            setCurrentIdx(0);
            await loadRecord(ids[0]);
          } else {
            resetForAdd();
          }
          setMode('browse');
        });
      },
    });
  }, [record, handleDelete, loadList, setCurrentIdx, loadRecord, resetForAdd, setMode]);

  const handleDetailChange = useCallback((idx: number, key: keyof FJ191, value: FJ191[keyof FJ191]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], [key]: value };
      }
      return next;
    });
  }, [setDetails]);

  const handleBoardNumbersChange = useCallback((detailIdx: number, boardNumbers: Partial<FJ192>[]) => {
    updateBoardNumbers(detailIdx, boardNumbers);
  }, [updateBoardNumbers]);

  // Approval status display
  const isApproved = record?.fj190_80 === 'P';
  const statusLabel = isApproved ? '已核准' : record?.fj190_01 ? '未核准' : null;

  return (
    <div className="j208-form">
      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <h1>研發樣品繳庫單 (pwj208f1)</h1>
          </div>
          <div className="page-title-right">
            <span style={{ fontSize: 12, color: '#999' }}>
              {record?.fj190_01 || '(新增)'}
            </span>
          </div>
        </div>

        {/* Form-level fields — FoxPro fj190_01 + fj190_11 are at form-level */}
        <div className="form-info-bar">
          <div className="field-group">
            <label data-contract-field="fj190_01">繳庫單號</label>
            <strong>{formData.fj190_01 || '--'}</strong>
          </div>
          <div className="field-group">
            <label data-contract-field="fj190_11">繳庫日期</label>
            {isEditing ? (
              <input
                type="date"
                value={(() => {
                  const v = formData.fj190_11;
                  if (!v) return '';
                  const s = String(v);
                  return s.length >= 10 ? s.substring(0, 10) : s;
                })()}
                onChange={e => setField('fj190_11', e.target.value || null)}
                style={{ maxWidth: 160, padding: '2px 6px', fontSize: 13 }} data-contract-field="fj190_11"
              />
            ) : (
              <strong>{formData.fj190_11 ? String(formData.fj190_11).substring(0, 10) : '--'}</strong>
            )}
          </div>
          <div className="field-group">
            <label data-contract-field="fj190_80">核准狀態</label>
            <strong>{statusLabel || '草稿'}</strong>
          </div>
        </div>

        {/* QuickSearch — FoxPro Pwtqseek1 */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <Input
            placeholder="繳庫單號查詢"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            disabled={isEditing}
            style={{ width: 200 }}
            allowClear
          />
          <Button
            icon={<SearchOutlined />}
            onClick={handleSearch}
            disabled={isEditing}
          >
            查詢
          </Button>
        </div>

        <div className="form-content">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: '1',
                label: '基本資料',
                children: (
                  <Tab1BasicInfo
                    isEditing={isEditing}
                    getField={getField}
                    setField={setField}
                  />
                ),
              },
              {
                key: '2',
                label: '產品明細',
                children: (
                  <Tab2Details
                    details={details}
                    isEditing={isEditing}
                    onAdd={addDetailRow}
                    onDelete={deleteDetailRow}
                    onDetailChange={handleDetailChange}
                    onBoardNumbersChange={handleBoardNumbersChange}
                    selectedDetailIdx={selectedDetailIdx}
                    onSelectDetail={setSelectedDetailIdx}
                    totalItems={formData.fj190_22 as number}
                  />
                ),
              },
            ]}
          />
        </div>

        {/* Spacer for fixed bottom bar */}
        <div style={{ height: 64 }} />

        {/* SharedActionBar — replaces page-local ActionBar */}
        <SharedActionBar
          mode={mode}
          isEditing={isEditing}
          hasRecord={!!record}
          currentIdx={currentIdx}
          recordCount={allIds.length}
          onNavFirst={() => navTo(0, isEditing)}
          onNavPrev={() => navTo(currentIdx - 1, isEditing)}
          onNavNext={() => navTo(currentIdx + 1, isEditing)}
          onNavLast={() => navTo(allIds.length - 1, isEditing)}
          onAdd={handleAdd}
          onModify={handleEdit}
          onDelete={handleDeleteAction}
          onSave={handleSaveAction}
          onCancel={handleCancel}
          onExit={() => navigate('/erp')}
          canCreate={canCreate}
          canEdit={canEdit && !isApproved}
          canDelete={canDelete && !isApproved}
          statusLabel={statusLabel}
        />
        <BatchAcceptanceBridge
          manifest={pwj208f1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record?.fj190_01 ? [record.fj190_01] : []}
          keyOf={(item) => item.fj190_01}
          queryExact={(key) => j208Api.get(key)}
          lastApiResponse={record}
          loading={loading}
          onAutoFill={() => allIds[0] && navTo(0, false)}
          autoFillDisabled={allIds.length === 0}
          screenDraft={{ master: formData, details }}
        />
      </div>
    </div>
  );
};

export default J208Form;
